import React, { memo, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

export const getConnectorSchedule = (lastRunTimestamp, intervalSeconds, currentTime) => {
  if (!Number.isFinite(lastRunTimestamp) || !lastRunTimestamp
    || !Number.isFinite(intervalSeconds) || intervalSeconds <= 0) return null;
  const intervalMilliseconds = intervalSeconds * 1000;
  const elapsedMilliseconds = currentTime - lastRunTimestamp;
  const completedCycles = Math.max(0, Math.floor(elapsedMilliseconds / intervalMilliseconds));
  const cycleStart = lastRunTimestamp + completedCycles * intervalMilliseconds;
  return {
    progress: Math.min(1, Math.max(0, (currentTime - cycleStart) / intervalMilliseconds)),
    remainingSeconds: Math.max(0, Math.ceil((cycleStart + intervalMilliseconds - currentTime) / 1000)),
  };
};

// Only the ring updates on the clock; the full node and its menus stay idle.
const ConnectorScheduleProgress = ({ lastRunTimestamp, intervalSeconds, t }) => {
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  useEffect(() => {
    let timer;
    const stop = () => window.clearInterval(timer);
    const resume = () => {
      stop();
      if (document.visibilityState === 'hidden') return;
      setCurrentTime(Date.now());
      // About one percentage point, capped at 30s for long schedules.
      const delay = Math.max(1000, Math.min(30000, intervalSeconds * 10));
      timer = window.setInterval(() => setCurrentTime(Date.now()), delay);
    };
    resume();
    document.addEventListener('visibilitychange', resume);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', resume);
    };
  }, [intervalSeconds, lastRunTimestamp]);

  const schedule = getConnectorSchedule(lastRunTimestamp, intervalSeconds, currentTime);
  if (!schedule) return null;
  return (
    <Box
      data-testid="connector-schedule-progress"
      sx={{
        position: 'absolute', top: -8, left: -8,
        width: 'calc(100% + 16px)', height: 'calc(100% + 16px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none', zIndex: 0,
      }}
    >
      <CircularProgress
        variant="determinate" value={100} size="100%" thickness={3}
        sx={{ position: 'absolute', color: 'rgba(87, 87, 176, 0.18)' }}
        aria-label={t('dag.tooltip.next_check_track', 'Next check progress track')}
      />
      <CircularProgress
        variant="determinate" value={schedule.progress * 100} size="100%" thickness={3}
        sx={{ position: 'absolute', color: '#7777cf' }}
        aria-label={t('dag.tooltip.next_check_progress', 'Progress to next check')}
      />
    </Box>
  );
};

export default memo(ConnectorScheduleProgress);
